import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import axios from "axios";

export default function AddEditProduct() {
  const { id } = useParams(); // if id exists → edit
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    imageUrl: "",
    description: "",
    about: "",
    keyFeatures: [],
    productCategory: "",
    productPrice: "",
  });

  const [featureInput, setFeatureInput] = useState("");
  const [categories, setCategories] = useState([]);

  /* ---------------- FETCH DATA ---------------- */

  useEffect(() => {
    // Fetch categories
    axios
      .get("https://api.livestockify.com/api/admin/product-category")
      .then((res) => setCategories(res.data))
      .catch(console.error);

    // Fetch product details (EDIT)
    if (isEdit) {
      axios
        .get(`https://api.livestockify.com/api/admin/products/${id}`)
        .then((res) => {
          const p = res.data;

          setForm({
            name: p.name || "",
            imageUrl: p.imageUrl || "",
            description: p.description || "",
            about: p.about || "",
            keyFeatures: Array.isArray(p.keyFeatures)
              ? p.keyFeatures
              : Array.isArray(p.keyFeaturesList)
              ? p.keyFeaturesList
              : typeof p.keyFeatures === "string"
              ? p.keyFeatures.split(",").map((f) => f.trim())
              : [],

            productCategory: p.productCategory?.name || "",
            productPrice: p.productPrice?.toString() || "",
          });
        })
        .catch(console.error);
    }
  }, [id, isEdit]);

  /* ---------------- HANDLERS ---------------- */

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addFeature = () => {
    if (!featureInput.trim()) return;

    setForm({
      ...form,
      keyFeatures: [...form.keyFeatures, featureInput.trim()],
    });

    setFeatureInput("");
  };

  const removeFeature = (index) => {
    setForm({
      ...form,
      keyFeatures: form.keyFeatures.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.productCategory || !form.productPrice) {
      alert("Please fill all required fields");
      return;
    }

    if (form.keyFeatures.length === 0) {
      alert("Please add at least one key feature");
      return;
    }

    const payload = {
      name: form.name,
      imageUrl: form.imageUrl,
      description: form.description,
      about: form.about,
      keyFeatures: form.keyFeatures,
      productCategory: form.productCategory.trim(),
      productPrice: Number(form.productPrice),
    };

    try {
      if (isEdit) {
        await axios.put(
          `https://api.livestockify.com/api/admin/products/${id}`,
          payload
        );
      } else {
        await axios.post(
          "https://api.livestockify.com/api/admin/products",
          payload
        );
      }

      navigate("/products-list");
    } catch (err) {
      console.error("API Error:", err.response?.data || err);
      alert("Failed to save product");
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="flex  min-h-screen bg-gray-100">
      <Sidebar />

      <div className="flex-1 bg-white rounded-l-2xl p-4 md:p-6">
        <h1 className="text-xl font-semibold mb-6 text-green-500">
          {isEdit ? "Edit Product" : "Add Product"}
        </h1>

        <div className="max-w-3xl mx-auto space-y-4">
          {/* Name */}
          <Input
            label="Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />

          {/* Image URL */}
          <Input
            label="Image URL"
            name="imageUrl"
            value={form.imageUrl}
            onChange={handleChange}
          />

          {/* Description */}
          <Textarea
            label="Description"
            name="description"
            value={form.description}
            onChange={handleChange}
          />

          {/* About */}
          <Input
            label="About"
            name="about"
            value={form.about}
            onChange={handleChange}
          />

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 md:gap-4">
            {/* Label */}
            <label className="text-sm font-medium text-gray-700 md:text-right">
              Key Features
            </label>

            {/* Right side content */}
            <div>
              {/* Input + Add button */}
              <div className="flex gap-2 w-full max-w-[520px]">
                <input
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  className="border px-3 py-2 rounded flex-1"
                  placeholder="Add feature"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="bg-gray-200 px-4 rounded whitespace-nowrap"
                >
                  Add
                </button>
              </div>

              {/* Feature list */}
              <div className="mt-2 space-y-1">
                {form.keyFeatures.map((f, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center border px-3 py-1 rounded text-sm"
                  >
                    {f}
                    <button
                      type="button"
                      onClick={() => removeFeature(i)}
                      className="text-red-500"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Category */}
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-2 md:gap-4">
            <label className="text-sm font-medium text-gray-700 md:text-right">
              Product Category
            </label>

            <select
              name="productCategory"
              value={form.productCategory}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.category}>
                  {c.category}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <Input
            label="Product Price"
            name="productPrice"
            value={form.productPrice}
            onChange={handleChange}
          />

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => navigate("/products-list")}
              className="border px-6 py-2 rounded"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              className="bg-green-500 text-white px-6 py-2 rounded"
            >
              {isEdit ? "Update" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- REUSABLE INPUTS ---------------- */

const Input = ({ label, ...props }) => (
  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-2 md:gap-4">
    <label className="text-sm font-medium text-gray-700 md:text-right">
      {label}
    </label>

    <input {...props} className="border px-3 py-2 rounded w-full" />
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-2 md:gap-4">
    <label className="text-sm font-medium text-gray-700 md:text-right pt-2">
      {label}
    </label>

    <textarea {...props} rows={4} className="border px-3 py-2 rounded w-full" />
  </div>
);
